// Users and passwords: the rules the forms check before the API does, a
// password generator and the messages for the API's error codes.
import { ApiError } from '../api/client.js';
import type { ManagedUser, TenantRole, TenantRoleInput } from '../api/types.js';

/** Same as central-api: new passwords need at least this many characters. */
export const MIN_PASSWORD_LENGTH = 10;
export const MAX_PASSWORD_LENGTH = 1000;

/** No look-alikes (0/O, 1/l/I), so a password read aloud or copied by hand works. */
const ALPHABET = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * A random password: `groups` groups of 4 characters joined with dashes
 * (4 × 4 from 56 characters ≈ 93 bits). Rejection sampling keeps it uniform.
 */
export function generatePassword(
  groups = 4,
  random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n)),
): string {
  const limit = 256 - (256 % ALPHABET.length);
  const out: string[] = [];
  while (out.length < groups * 4) {
    for (const b of random(32)) {
      if (b < limit && out.length < groups * 4) out.push(ALPHABET[b % ALPHABET.length] ?? '');
    }
  }
  const parts: string[] = [];
  for (let i = 0; i < groups; i++) parts.push(out.slice(i * 4, i * 4 + 4).join(''));
  return parts.join('-');
}

/** Why a new password cannot be used yet; null = fine. */
export function newPasswordError(password: string, confirm?: string): string | null {
  const n = Array.from(password).length;
  if (n < MIN_PASSWORD_LENGTH) {
    return `At least ${String(MIN_PASSWORD_LENGTH)} characters (${String(n)} so far).`;
  }
  if (n > MAX_PASSWORD_LENGTH) return `At most ${String(MAX_PASSWORD_LENGTH)} characters.`;
  if (confirm !== undefined && confirm !== password) return 'The passwords do not match.';
  return null;
}

/** The change-password form: what blocks submitting; null = ready. */
export function changePasswordError(f: {
  current: string;
  next: string;
  confirm: string;
}): string | null {
  if (!f.current) return 'Enter your current password.';
  const err = newPasswordError(f.next, f.confirm);
  if (err) return err;
  if (f.next === f.current) return 'The new password is the same as the current one.';
  return null;
}

/** Plain messages for the API's `code`s; null = use the API's own text. */
export function userErrorMessage(code: string | null): string | null {
  switch (code) {
    case 'wrong_password':
      return 'The current password is wrong.';
    case 'too_many_attempts':
      return 'Too many wrong passwords. Wait a few minutes and try again.';
    case 'password_too_short':
      return `The new password needs at least ${String(MIN_PASSWORD_LENGTH)} characters.`;
    case 'password_unchanged':
      return 'The new password is the same as the current one.';
    case 'email_taken':
      return 'A user with this e-mail already exists.';
    case 'self':
      return 'You cannot disable, delete or change the roles of your own account.';
    case 'last_admin':
      return 'This is the last enabled platform admin; add or enable another one first.';
    case 'role_required':
      return 'Give the user a role in a tenant (or make them platform staff).';
    case 'unknown_tenant':
      return 'You cannot give roles in this tenant.';
    case 'unknown_sites':
      return 'Some of the sites are not in the tenant or not in your own access.';
    case 'sites_required':
      return 'Your access is limited to some sites: choose sites for the user.';
    case 'platform_only':
      return 'Only platform staff can give or take the platform role.';
    case 'other_tenant':
      return 'The user also belongs to another tenant; ask IDevelopment.';
    case 'duplicate_tenant':
      return 'Each tenant can be given once.';
    case 'session_required':
      return 'Sign in to change your password.';
    default:
      return null;
  }
}

/** The API's message for a failed user request, in plain words where the code is known. */
export function userErrorText(err: unknown): string {
  if (err instanceof ApiError) return userErrorMessage(err.code) ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

type Guarded = Pick<ManagedUser, 'id' | 'disabled' | 'platform_role'>;

/**
 * Why the row's Disable/Delete is not offered; null = offered. Mirrors the
 * API's guards (yourself, the last enabled platform admin).
 */
export function removalBlocked(
  u: Guarded,
  users: readonly Guarded[],
  selfId: string | undefined,
): string | null {
  if (u.id === selfId) return 'This is you.';
  if (
    u.platform_role &&
    !u.disabled &&
    !users.some((x) => x.id !== u.id && !x.disabled && x.platform_role)
  ) {
    return 'The last enabled platform admin.';
  }
  return null;
}

// --- roles (ADR 0010) ---------------------------------------------------------------

export const ROLE_LABELS: Record<TenantRole, string> = {
  viewer: 'Viewer',
  operator: 'Operator',
  tenant_admin: 'Tenant admin',
};

/** What each role may do, for the role picker. */
export const ROLE_HELP: Record<TenantRole, string> = {
  viewer: 'sites, points, trends, alarms (read only)',
  operator: 'viewer + acknowledge alarms, rules, holidays, tags and limits',
  tenant_admin: 'operator + users and API tokens of the tenant',
};

/** The roles of a user as the editor holds them. */
export interface RolesValue {
  platform: boolean;
  /** Per tenant id: the role, and the sites (null = all sites of the tenant). */
  tenants: Record<string, { role: TenantRole; siteIds: string[] | null } | undefined>;
}

export function rolesOf(u: Pick<ManagedUser, 'platform_role' | 'tenants'>): RolesValue {
  const tenants: RolesValue['tenants'] = {};
  for (const t of u.tenants) tenants[t.tenant_id] = { role: t.role, siteIds: t.site_ids };
  return { platform: u.platform_role === 'platform_admin', tenants };
}

/** The API body for these roles. `withPlatform` = the caller may set the platform role. */
export function rolesBody(
  v: RolesValue,
  withPlatform: boolean,
): { platform_role?: 'platform_admin' | null; tenants: TenantRoleInput[] } {
  const tenants: TenantRoleInput[] = [];
  for (const [tenantId, t] of Object.entries(v.tenants)) {
    if (t) tenants.push({ tenant_id: tenantId, role: t.role, site_ids: t.siteIds });
  }
  return {
    ...(withPlatform && { platform_role: v.platform ? ('platform_admin' as const) : null }),
    tenants,
  };
}

/** Why the roles cannot be saved; null = fine. */
export function rolesError(v: RolesValue): string | null {
  const given = Object.values(v.tenants).filter((t) => t !== undefined);
  if (!v.platform && given.length === 0) return 'Choose a role in a tenant.';
  if (given.some((t) => t.siteIds?.length === 0)) return 'Choose at least one site.';
  return null;
}

/** One line per role for the table, e.g. `Hotels: Viewer (2 sites)`. */
export function roleSummary(u: Pick<ManagedUser, 'platform_role' | 'tenants'>): string[] {
  const lines = u.platform_role ? ['Platform admin'] : [];
  for (const t of u.tenants) {
    const sites =
      t.site_ids === null
        ? ''
        : ` (${String(t.site_ids.length)} site${t.site_ids.length === 1 ? '' : 's'})`;
    lines.push(`${t.tenant_name}: ${ROLE_LABELS[t.role]}${sites}`);
  }
  return lines.length ? lines : ['No role'];
}

/** Users sorted for the table: enabled first, then by e-mail. */
export function sortUsers<T extends Pick<ManagedUser, 'email' | 'disabled'>>(
  users: readonly T[],
): T[] {
  return [...users].sort(
    (a, b) => Number(a.disabled) - Number(b.disabled) || a.email.localeCompare(b.email),
  );
}
